import getStore from "src/utils/redux/store";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import { acceptedNoomClinicalOffer } from "src/utils/userSegment/features";
import { appConfig } from "src/config";
import { setPrefetchedEltv } from "src/utils/redux/slices/checkoutOrders";
import { fetchEltv, ComputeEltvRequest } from "src/utils/api/marketingApi";
import { createBuyflowPurchaseIntentEvent } from "src/utils/services/ConversionTracker";
import { trackEvent } from "src/utils/api/tracker";
import { captureException } from "src/utils/error";

function buildEltvRequestFromState(): ComputeEltvRequest | null {
  const state = getStore().getState();
  const { recommendedPlan, userData, paymentEnrollmentForm, checkoutOrders } =
    state;

  const email = paymentEnrollmentForm?.enrollmentInfo?.email;
  if (!email) {
    return null;
  }

  const surveyAnswers = getSurveyAnswers(state);
  const { initialBillingCycle, recurringBillingCycle } = checkoutOrders;

  const intentEvent = createBuyflowPurchaseIntentEvent(
    email,
    recommendedPlan,
    surveyAnswers,
    userData,
    initialBillingCycle,
    recurringBillingCycle
  );

  return {
    userId: intentEvent.userId,
    email: intentEvent.email,
    age: intentEvent.age,
    gender: intentEvent.gender,
    countryCode: intentEvent.countryCode,
    state: intentEvent.state,
    currentBmi: intentEvent.currentBmi,
    hasHealthRisk: intentEvent.hasHealthRisk,
    hasImportantDate: intentEvent.hasImportantDate,
    tookWeightLossMeds: intentEvent.tookWeightLossMeds,
    weightLossGoal: intentEvent.weightLossGoal,
    planId: intentEvent.planId,
    planRecurringPrice: intentEvent.planRecurringPrice,
    planBillingCycle: intentEvent.planBillingCycle,
    trialPeriod: intentEvent.trialPeriod,
    paidAtSignup: intentEvent.paidAtSignup,
    trialFee: intentEvent.trialFee,
    discount: intentEvent.discount,
  };
}

/**
 * Pre-fetches the ML-computed ELTV from the marketing service for med users.
 * Runs as fire-and-forget after checkout initialization. The result is stored
 * in Redux for later use during the T&C agreement (shadow-mode logging).
 */
export async function prefetchEltvIfEligible(): Promise<void> {
  if (!acceptedNoomClinicalOffer() || !appConfig.MED_PIXEL_TRACKING_ENABLED) {
    return;
  }

  getStore().dispatch(setPrefetchedEltv(undefined));

  try {
    const request = buildEltvRequestFromState();
    if (!request) {
      getStore().dispatch(setPrefetchedEltv(null));
      return;
    }

    const response = await fetchEltv(request);
    getStore().dispatch(setPrefetchedEltv({ eltvValue: response.eltvValue }));
    trackEvent("EltvPrefetchCompleted", {
      eltvValue: response.eltvValue,
      planId: request.planId,
    });
  } catch (err) {
    getStore().dispatch(setPrefetchedEltv(null));
    captureException(err);
    trackEvent("EltvPrefetchFailed", {
      error: err?.message || "unknown",
    });
  }
}
